import { useState, useEffect } from 'react';
import { useSchemaOrg, getLocalBusinessSchema, getWebsiteSchema, getOrganizationSchema, getMenuSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [scrolled, setScrolled] = useState(false);

  // Add Schema.org structured data
  useSchemaOrg([
    getLocalBusinessSchema(),
    getWebsiteSchema(),
    getOrganizationSchema(),
    getMenuSchema(),
    getBreadcrumbSchema([
      { name: 'Home', url: '/' }
    ])
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll function
  const smoothScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const products = [
    // Daily Soup
    { id: 1, name: 'Lentil Soup', category: 'soup', price: '£6.50', description: 'Hearty and nutritious lentil soup (GF/V)' },
    { id: 2, name: 'Mushroom Soup', category: 'soup', price: '£6.50', description: 'Creamy mushroom soup with rich flavors' },
    
    // Lunch Items
    { id: 4, name: 'Caesar Salad', category: 'lunch', price: '£8.50', description: 'Classic Caesar with grilled chicken' },
    { id: 5, name: 'Turkish Ravioli Manti', category: 'lunch', price: '£10.00', description: 'Served with yogurt & spicy hotter sauce' },
    
    // Cakes
    { id: 6, name: 'Special San Sebastian', category: 'cakes', price: '£5.50', description: 'With chocolate sauce' },
    { id: 7, name: 'Lotus Biscoff Cheesecake', category: 'cakes', price: '£5.00', description: 'Creamy Biscoff flavored cheesecake' },
    { id: 8, name: 'Cheesecake', category: 'cakes', price: '£5.00', description: 'Classic creamy cheesecake' },
    { id: 44, name: 'Carrot Cake', category: 'cakes', price: '£5.00', description: 'Moist carrot cake with cream cheese frosting' },
    { id: 49, name: 'Red Velvet Cake', category: 'cakes', price: '£5.50', description: 'Classic red velvet with cream cheese frosting' },
    
    // Vegan and Gluten Free Options
    { id: 9, name: 'Roasted Tomato & Hummus on Sourdough', category: 'vegan', price: '£5.50', description: 'Vegan and gluten-free option' },
    { id: 10, name: 'Brie, Honey, Grapes on Sourdough', category: 'vegan', price: '£6.00', description: 'Gluten-free sourdough option' },
    { id: 11, name: 'Warm Pitta with Hummus & Vegetables', category: 'vegan', price: '£5.50', description: 'Vegan and gluten-free option' },
    { id: 12, name: 'Roasted Cherry Tomatoes, Avocado, 2 Eggs with Sourdough Toast', category: 'vegan', price: '£9.20', description: 'Gluten-free option available' },
    { id: 13, name: 'Bacon & Avocado, 2 Eggs with Sourdough Toast', category: 'vegan', price: '£9.80', description: 'Gluten-free option available' },
    { id: 14, name: 'Roasted Mushrooms, Cherry Tomatoes, 2 Eggs with Sourdough Toast', category: 'vegan', price: '£9.00', description: 'Gluten-free option available' },
    { id: 15, name: 'Roasted Mushrooms & Cherry Tomatoes with Sourdough Toast', category: 'vegan', price: '£7.30', description: 'Vegan and gluten-free option' },
    
    // Coffee Items
    { id: 16, name: 'Espresso', category: 'coffee', price: '£2.80', description: 'Rich and bold single shot' },
    { id: 17, name: 'Cappuccino', category: 'coffee', price: '£3.50', description: 'Perfect foam art masterpiece' },
    { id: 18, name: 'Latte', category: 'coffee', price: '£3.80', description: 'Smooth and creamy delight' },
    { id: 19, name: 'Americano', category: 'coffee', price: '£3.20', description: 'Classic bold coffee' },
    { id: 20, name: 'Flat White', category: 'coffee', price: '£3.60', description: 'Velvety microfoam perfection' },
    { id: 21, name: 'Macchiato', category: 'coffee', price: '£3.00', description: 'Espresso with foam kiss' },
    { id: 22, name: 'Iced Coffee', category: 'coffee', price: '£3.80', description: 'Refreshingly chilled' },
    { id: 23, name: 'Cold Brew', category: 'coffee', price: '£4.00', description: 'Smooth 24-hour brew' },
    { id: 24, name: 'Caramel Latte', category: 'coffee', price: '£4.50', description: 'Sweet caramel swirl' },
    { id: 25, name: 'Matcha Latte', category: 'coffee', price: '£4.80', description: 'Japanese green tea blend' },
    { id: 26, name: 'Chai Latte', category: 'coffee', price: '£4.20', description: 'Spiced aromatic warmth' },
    { id: 27, name: 'Hot Chocolate', category: 'coffee', price: '£3.80', description: 'Rich cocoa indulgence' },
    
    // Smoothies
    { id: 32, name: 'Strawberry Smoothie', category: 'smoothies', price: '£5.50', description: 'Fresh strawberry blend' },
    { id: 33, name: 'Mango Smoothie', category: 'smoothies', price: '£5.50', description: 'Tropical mango delight' },
    { id: 34, name: 'Banana Smoothie', category: 'smoothies', price: '£5.50', description: 'Creamy banana blend' },
    { id: 35, name: 'Mixed Berry Smoothie', category: 'smoothies', price: '£5.50', description: 'Berry medley perfection' },
    
    // Milkshakes
    { id: 36, name: 'Vanilla Milkshake', category: 'milkshakes', price: '£5.50', description: 'Classic vanilla cream' },
    { id: 37, name: 'Chocolate Milkshake', category: 'milkshakes', price: '£5.50', description: 'Rich chocolate indulgence' },
    { id: 38, name: 'Strawberry Milkshake', category: 'milkshakes', price: '£5.50', description: 'Sweet strawberry cream' },
    { id: 39, name: 'Oreo Milkshake', category: 'milkshakes', price: '£5.50', description: 'Cookies and cream dream' },
    
    // Fresh Juices
    { id: 40, name: 'Orange Juice', category: 'juices', price: '£4.50', description: 'Freshly squeezed oranges' },
    { id: 41, name: 'Apple Juice', category: 'juices', price: '£4.50', description: 'Crisp apple freshness' },
    { id: 42, name: 'Carrot Juice', category: 'juices', price: '£4.50', description: 'Fresh carrot blend' },
    { id: 43, name: 'Mixed Fruit Juice', category: 'juices', price: '£4.50', description: 'Tropical fruit medley' },
    
    // Pastries
    { id: 28, name: 'Croissant', category: 'pastry', price: '£2.80', description: 'Buttery flaky layers' },
    { id: 29, name: 'Blueberry Muffin', category: 'pastry', price: '£3.20', description: 'Fresh baked goodness' },
    { id: 30, name: 'Chocolate Cake', category: 'pastry', price: '£4.50', description: 'Decadent chocolate layers' },
    { id: 31, name: 'Cinnamon Roll', category: 'pastry', price: '£3.50', description: 'Glazed cinnamon swirl' },
    
    // Toasties
    { id: 50, name: 'Cheese & Ham Toastie', category: 'toasties', price: '£6.20', description: 'Classic grilled cheese and ham' },
    { id: 51, name: 'Cheese & Tomato Toastie', category: 'toasties', price: '£6.20', description: 'Fresh tomato and melted cheese' },
    { id: 52, name: 'Cheese & Caramelised Onion Chutney Toastie', category: 'toasties', price: '£6.20', description: 'Sweet caramelised onion with cheese' },
    
    // Paninis
    { id: 53, name: 'Chicken Salami, Tomato & Cheese Panini', category: 'paninis', price: '£7.00', description: 'Grilled panini with chicken salami' },
    { id: 54, name: 'Mozzarella, Tomato & Pesto Panini', category: 'paninis', price: '£7.00', description: 'Fresh mozzarella with basil pesto' },
    { id: 55, name: 'Chicken, Mozzarella & Pesto Panini', category: 'paninis', price: '£7.00', description: 'Grilled chicken with mozzarella' },
    { id: 56, name: 'Tuna, Sweetcorn & Mayo Panini', category: 'paninis', price: '£7.00', description: 'Classic tuna melt panini' },
    { id: 57, name: 'Cheese & Ham Panini', category: 'paninis', price: '£7.00', description: 'Traditional ham and cheese' },
    { id: 58, name: 'Hot Sausages in Panini', category: 'paninis', price: '£7.00', description: 'Grilled sausages in panini bread' },
    
    // On Toast
    { id: 59, name: 'Scrambled Eggs on Toast (GFA)', category: 'toast', price: '£6.00', description: 'Fluffy scrambled eggs on toast' },
    { id: 60, name: 'Beans on Toast (GFA)', category: 'toast', price: '£5.00', description: 'Classic baked beans on toast' },
    { id: 61, name: 'Poached Eggs on Toast (GFA)', category: 'toast', price: '£6.50', description: 'Perfectly poached eggs' },
    { id: 62, name: '2 Slices Toast with Homemade Jam or Marmalade and Honey (GFA)', category: 'toast', price: '£4.00', description: 'Toast with homemade preserves' },
    { id: 63, name: 'Extra Egg', category: 'toast', price: '£2.00', description: 'Add an extra egg' },
    { id: 64, name: 'Extra Bacon', category: 'toast', price: '£2.00', description: 'Add crispy bacon' },
    { id: 65, name: 'Turkish Pastry', category: 'toast', price: '£7.90', description: 'With fresh tomato, cucumber & olives' },
    { id: 66, name: 'Turkish Simit Sandwich', category: 'toast', price: '£5.50', description: 'With cheese & tomato' },
    { id: 67, name: 'Pancakes with Chocolate Sauce or Honey with Fruit', category: 'toast', price: '£7.50', description: 'Fluffy pancakes with toppings' },
    { id: 68, name: 'Extra Ice Cream', category: 'toast', price: '£2.00', description: 'Add a scoop of ice cream' },
    
    // Breakfast Items
    { id: 69, name: 'Veggie Breakfast', category: 'breakfast', price: '£11.50', description: '2 eggs, 2 veggie sausages, mushrooms, tomato, hash browns, beans, avocado, rocket with toast' },
    { id: 70, name: 'Vegan Breakfast', category: 'breakfast', price: '£10.00', description: '2 vegan sausages, mushrooms, tomato, 2 hash browns, avocado, rocket with sourdough toast' },
    { id: 71, name: 'Cherryleaf English Breakfast', category: 'breakfast', price: '£13.00', description: '2 sausages, 2 eggs, 2 bacon, mushrooms, tomatoes, beans and hash browns with butter on toast' },
    { id: 72, name: 'Cherryleaf Turkish Breakfast', category: 'breakfast', price: '£13.50', description: '2 eggs, turkish beef pepperoni, grilled halloumi, mushrooms, tomato, avocado, cucumber, olives, butter, honey and homemade jam with bread and rocket' },
    { id: 73, name: 'Menemen Special', category: 'breakfast', price: '£9.50', description: '2 eggs, tomato, peppers, spices, cheese with bread. Extra Turkish Pepperoni £2.00' },
    
    // Omelettes
    { id: 74, name: '2 Eggs, Cheese with Salad Omelette', category: 'omelettes', price: '£7.00', description: 'Classic cheese omelette with salad' },
    { id: 75, name: '2 Eggs, Ham, Mushrooms & Cheese with Salad Omelette', category: 'omelettes', price: '£8.00', description: 'Hearty ham and mushroom omelette' },
    { id: 76, name: 'Plain Porridge (GFA) (V)', category: 'omelettes', price: '£3.75', description: 'Warm creamy porridge' },
    { id: 77, name: 'Topped Porridges (GFA) (V)', category: 'omelettes', price: '£4.75', description: 'a) Banana, flaked almonds and honey, with nutmeg sprinkles. b) Mixed berries, oven roasted seeds with maple syrup. c) Dried cranberries, raisins and blueberries with mixed seeds and honey or syrup drizzle. Almond, soya, oat or coconut milk option +£0.30. Additional pecans/walnuts/pistachios +£0.50' },
    { id: 78, name: 'Morning Power Up (GFA)(V)', category: 'omelettes', price: '£6.00', description: 'Yoghurt, mixed berries, nuts and seeds, with maple syrup or honey. Add banana +£0.30' },
  ];

  const features = [
    {
      icon: 'ri-leaf-line',
      title: 'Ethically Sourced',
      description: 'We partner with sustainable farms to bring you the finest organic coffee beans'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Expert Baristas',
      description: 'Our trained professionals craft each cup with precision and passion'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Cozy Atmosphere',
      description: 'A welcoming space designed for comfort, conversation, and connection'
    },
    {
      icon: 'ri-cake-3-line',
      title: 'Fresh Pastries',
      description: 'Baked daily in-house using traditional recipes and premium ingredients'
    }
  ];

  // Google Reviews from Google Maps
  const testimonials = [
    {
      name: 'Local Customer',
      role: 'Google Review',
      text: 'Cherry Leaf has become my daily ritual. The coffee is exceptional, and the atmosphere is perfect for both work and relaxation. The staff remembers my order and always greets me with a smile!',
      rating: 5,
      source: 'Google Maps'
    },
    {
      name: 'Norwich Resident',
      role: 'Google Review',
      text: 'I hold all my client meetings here. The quality of coffee is outstanding, and the ambiance strikes the perfect balance between professional and welcoming. Highly recommend!',
      rating: 5,
      source: 'Google Maps'
    },
    {
      name: 'Coffee Lover',
      role: 'Google Review',
      text: 'As someone who takes coffee seriously, I can confidently say Cherry Leaf serves the best espresso in Norwich. Their attention to detail and commitment to quality is unmatched.',
      rating: 5,
      source: 'Google Maps'
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#030406]">
      {/* Navigation */}
      <nav className={`w-full fixed top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#030406] shadow-lg py-2' : 'bg-[#030406]/95 backdrop-blur-sm py-3'
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="#home" onClick={(e) => smoothScrollTo(e, '#home')} className="cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/2c6a859d2742927cf449d83a19381c12/4c6b31ee4e46e56bb1fe230ca6c02c08.jpeg" 
              alt="Cherry Leaf Coffee Shop Logo" 
              className="h-16 w-auto object-contain"
            />
          </a>
          
          <div className="flex items-center gap-8">
            <a href="#home" onClick={(e) => smoothScrollTo(e, '#home')} className="text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap font-medium">Home</a>
            <a href="#about" onClick={(e) => smoothScrollTo(e, '#about')} className="text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap font-medium">About</a>
            <a href="#menu" onClick={(e) => smoothScrollTo(e, '#menu')} className="text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap font-medium">Menu</a>
            <a href="#testimonials" onClick={(e) => smoothScrollTo(e, '#testimonials')} className="text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap font-medium">Reviews</a>
            <a href="#contact" onClick={(e) => smoothScrollTo(e, '#contact')} className="text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap font-medium">Contact</a>
            
            <div className="flex items-center gap-3 ml-6 border-l border-yellow-400/30 pl-6">
              <a 
                href="https://www.instagram.com/cherryleaf_norwich" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-yellow-400 hover:text-yellow-300 hover:scale-110 transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-fill text-xl"></i>
              </a>
              <a 
                href="https://x.com/cherryleafcoffe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-yellow-400 hover:text-yellow-300 hover:scale-110 transition-all cursor-pointer"
                aria-label="X (Twitter)"
              >
                <i className="ri-twitter-x-fill text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="home" 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/21e0bdfcc1f05981f12e701a6abdce29.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-[#030406]/80"></div>
        <div className="relative z-10 text-center text-yellow-400 px-6 max-w-5xl">
          <div className="mb-6 inline-block">
            <span className="bg-yellow-400 text-[#030406] px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
              Norwich's Best Coffee Shop & Breakfast Spot
            </span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Pacifico", serif' }}>
            Welcome to Cherry Leaf
          </h1>
          <p className="text-xl md:text-2xl mb-6 leading-relaxed max-w-3xl mx-auto font-light">
            <strong>Norwich's premier coffee shop</strong> serving exceptional artisan coffee, delicious breakfast, and fresh pastries. Experience the best coffee in Norwich at our St Giles Street location in the city centre.
          </p>
          <div className="flex gap-4 justify-center flex-wrap mb-8">
            <div className="bg-yellow-400 text-[#030406] px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
              <i className="ri-leaf-line"></i>
              Vegan Options Available
            </div>
            <div className="bg-yellow-400 text-[#030406] px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
              <i className="ri-checkbox-circle-line"></i>
              Gluten-Free Options
            </div>
          </div>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="#menu" 
              onClick={(e) => smoothScrollTo(e, '#menu')}
              className="inline-block bg-yellow-400 hover:bg-yellow-300 text-[#030406] px-10 py-4 rounded-full text-lg font-bold transition-all hover:scale-105 shadow-xl cursor-pointer whitespace-nowrap"
            >
              Explore Our Menu
            </a>
            <a 
              href="#contact" 
              onClick={(e) => smoothScrollTo(e, '#contact')}
              className="inline-block bg-transparent border-2 border-yellow-400 hover:bg-yellow-400/10 text-yellow-400 px-10 py-4 rounded-full text-lg font-bold transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Find Us
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#030406]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="text-center p-8 rounded-2xl bg-[#030406] border-2 border-yellow-400/20 hover:border-yellow-400 hover:shadow-xl hover:shadow-yellow-400/20 transition-all hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full mx-auto mb-6 text-3xl">
                  <i className={feature.icon}></i>
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-3">{feature.title}</h3>
                <p className="text-yellow-400/80 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
          
          {/* Vegan & Gluten-Free Highlight */}
          <div className="mt-16 bg-[#030406] border-2 border-yellow-400/30 rounded-3xl p-10 shadow-lg">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-yellow-400 mb-4">Special Dietary Options</h3>
              <p className="text-lg text-yellow-400/80 max-w-3xl mx-auto">
                We cater to all dietary preferences with our extensive <strong>vegan</strong> and <strong>gluten-free</strong> menu options
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#030406] border border-yellow-400/20 rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-yellow-400/10 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full text-2xl">
                    <i className="ri-leaf-line"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-yellow-400">Vegan Friendly</h4>
                </div>
                <p className="text-yellow-400/80 leading-relaxed mb-4">
                  Enjoy our delicious plant-based options including vegan breakfast, sourdough toasts, warm pitta with hummus, and more. All clearly marked on our menu.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">Vegan Breakfast</span>
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">Plant-Based</span>
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">Dairy-Free</span>
                </div>
              </div>
              <div className="bg-[#030406] border border-yellow-400/20 rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-yellow-400/10 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full text-2xl">
                    <i className="ri-checkbox-circle-line"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-yellow-400">Gluten-Free Available</h4>
                </div>
                <p className="text-yellow-400/80 leading-relaxed mb-4">
                  Many of our dishes can be prepared gluten-free, including sourdough toast options, eggs on toast, porridge, and more. Look for the (GFA) or (GF) markers.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">GF Sourdough</span>
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">GF Breakfast</span>
                  <span className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-full text-sm font-semibold">GF Porridge</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-[#030406]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block mb-4">
                <span className="bg-yellow-400 text-[#030406] px-4 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
                  Our Story
                </span>
              </div>
              <h2 className="text-5xl font-bold text-yellow-400 mb-6 leading-tight">Norwich's Favorite Coffee Shop & Breakfast Destination</h2>
              <p className="text-lg text-yellow-400/80 mb-6 leading-relaxed">
                Located in the heart of <strong>Norwich city centre</strong> at 50 St Giles Street, <strong>Cherry Leaf</strong> is your go-to destination for 
                exceptional <strong>coffee in Norwich</strong>. Whether you're looking for a perfect morning <strong>breakfast in Norwich</strong>, 
                a midday coffee break, or a cozy spot to meet friends, our <strong>Norwich coffee shop</strong> welcomes you with open arms.
              </p>
              <p className="text-lg text-yellow-400/80 mb-6 leading-relaxed">
                As one of the top-rated <strong>Norwich coffeeshops</strong>, we take pride in serving only the finest <strong>artisan coffee</strong> 
                made by our expert baristas. Every cup is crafted with precision using <strong>ethically-sourced premium beans</strong>, 
                ensuring you get the best coffee experience in Norwich every single time.
              </p>
              <p className="text-lg text-yellow-400/80 mb-8 leading-relaxed">
                Our commitment to quality extends beyond coffee. From our freshly baked <strong>breakfast pastries</strong> to our handcrafted 
                specialty drinks, everything at our <strong>Norwich cafe</strong> is made with love and attention to detail. Visit us 
                Monday to Saturday, 8am to 4pm, and discover why locals call us the <strong>best coffee shop in Norwich</strong>.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-yellow-400/30">
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-400 mb-2">5+</div>
                  <div className="text-sm text-yellow-400/70 font-medium">Years Serving Norwich</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-400 mb-2">50K+</div>
                  <div className="text-sm text-yellow-400/70 font-medium">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-400 mb-2">30+</div>
                  <div className="text-sm text-yellow-400/70 font-medium">Menu Items</div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-full h-full bg-yellow-400/10 rounded-3xl"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=A%20professional%20barista%20carefully%20preparing%20latte%20art%20in%20a%20white%20cup%2C%20focused%20expression%2C%20warm%20coffee%20shop%20interior%20with%20wooden%20counter%20and%20coffee%20equipment%2C%20natural%20lighting%2C%20high%20quality%20professional%20photography%2C%20inviting%20atmosphere&width=600&height=800&seq=22&orientation=portrait"
                  alt="Expert barista at Cherry Leaf Norwich coffee shop"
                  className="relative w-full h-[600px] object-cover object-top rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-24 px-6 bg-[#030406]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-yellow-400 text-[#030406] px-4 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
                Our Menu
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-yellow-400 mb-6">Coffee & Lunch Menu</h2>
            <p className="text-xl text-yellow-400/80 max-w-3xl mx-auto leading-relaxed mb-6">
              From classic <strong>Norwich coffee</strong> favorites to delicious <strong>lunch options</strong> and fresh cakes, 
              each item is crafted with premium ingredients at our Norwich city centre location
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <div className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <i className="ri-leaf-line"></i>
                (V) = Vegan
              </div>
              <div className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>
                (GF) = Gluten Free
              </div>
              <div className="bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <i className="ri-information-line"></i>
                (GFA) = Gluten Free Available
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex justify-center gap-3 mb-16 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'all'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              All Items
            </button>
            <button
              onClick={() => setSelectedCategory('breakfast')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'breakfast'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Breakfast
            </button>
            <button
              onClick={() => setSelectedCategory('omelettes')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'omelettes'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Omelettes & Porridge
            </button>
            <button
              onClick={() => setSelectedCategory('toasties')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'toasties'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Toasties
            </button>
            <button
              onClick={() => setSelectedCategory('paninis')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'paninis'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Paninis
            </button>
            <button
              onClick={() => setSelectedCategory('toast')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'toast'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              On Toast
            </button>
            <button
              onClick={() => setSelectedCategory('coffee')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'coffee'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Coffee & Drinks
            </button>
            <button
              onClick={() => setSelectedCategory('soup')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'soup'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Daily Soup
            </button>
            <button
              onClick={() => setSelectedCategory('lunch')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'lunch'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Lunch
            </button>
            <button
              onClick={() => setSelectedCategory('vegan')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'vegan'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Vegan & Gluten Free
            </button>
            <button
              onClick={() => setSelectedCategory('cakes')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'cakes'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Cakes
            </button>
            <button
              onClick={() => setSelectedCategory('smoothies')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'smoothies'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Smoothies
            </button>
            <button
              onClick={() => setSelectedCategory('milkshakes')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'milkshakes'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Milkshakes
            </button>
            <button
              onClick={() => setSelectedCategory('juices')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'juices'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Fresh Juices
            </button>
            <button
              onClick={() => setSelectedCategory('pastry')}
              className={`px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'pastry'
                  ? 'bg-yellow-400 text-[#030406] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'bg-[#030406] border border-yellow-400/30 text-yellow-400 hover:border-yellow-400'
              }`}
            >
              Pastries
            </button>
          </div>

          {/* Products Grid - Text Only */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <div 
                key={product.id}
                className="bg-[#030406] border-2 border-yellow-400/20 hover:border-yellow-400 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:shadow-yellow-400/20 transition-all hover:-translate-y-2 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-yellow-400 flex-1 pr-3">{product.name}</h3>
                  <div className="bg-yellow-400 text-[#030406] px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                    {product.price}
                  </div>
                </div>
                <p className="text-yellow-400/80 leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-6 bg-[#030406]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-yellow-400 text-[#030406] px-4 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
                Google Reviews
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-yellow-400 mb-6">What Our Customers Say</h2>
            <p className="text-xl text-yellow-400/80 max-w-3xl mx-auto">
              Real reviews from our valued customers on Google Maps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-[#030406] border-2 border-yellow-400/20 hover:border-yellow-400 rounded-2xl p-8 shadow-lg hover:shadow-xl hover:shadow-yellow-400/20 transition-all hover:-translate-y-2 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-yellow-400/70">
                    <i className="ri-google-fill text-lg"></i>
                    <span>{testimonial.source}</span>
                  </div>
                </div>
                <p className="text-yellow-400/80 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4 pt-6 border-t border-yellow-400/20">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-yellow-400">{testimonial.name}</div>
                    <div className="text-sm text-yellow-400/70">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://www.google.com/maps?q=Cherry+Leaf,+50+St+Giles+St,+Norwich+NR2+1LW"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-[#030406] px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-google-fill text-2xl"></i>
              View All Reviews on Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-[#030406]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-yellow-400 text-[#030406] px-4 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
                Visit Us
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-yellow-400 mb-6">Visit Our Norwich Coffee Shop</h2>
            <p className="text-xl text-yellow-400/80 max-w-3xl mx-auto">
              Located in the heart of Norwich at 50 St Giles Street, we're ready to serve you the perfect cup
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-[#030406] border-2 border-yellow-400/30 rounded-2xl shadow-xl p-10">
              <h3 className="text-3xl font-bold text-yellow-400 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full">
                  <i className="ri-map-pin-fill text-xl"></i>
                </div>
                Location & Hours
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-yellow-400 mb-2 text-lg">Address</h4>
                  <p className="text-yellow-400/80 text-lg">50 St Giles Street</p>
                  <p className="text-yellow-400/80 text-lg">Norwich NR2 1LW</p>
                  <p className="text-yellow-400/80 text-lg">United Kingdom</p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow-400 mb-3 text-lg">Opening Hours</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-[#030406] border border-yellow-400/20 rounded-lg px-4 py-3">
                      <span className="text-yellow-400/80 font-medium">Monday - Friday</span>
                      <span className="text-yellow-400 font-bold">8:30 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center bg-[#030406] border border-yellow-400/20 rounded-lg px-4 py-3">
                      <span className="text-yellow-400/80 font-medium">Saturday</span>
                      <span className="text-yellow-400 font-bold">9:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center bg-[#030406] border-2 border-red-400/30 rounded-lg px-4 py-3">
                      <span className="text-yellow-400/80 font-medium">Sunday</span>
                      <span className="text-red-400 font-bold">Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#030406] border-2 border-yellow-400/30 rounded-2xl shadow-xl p-10">
              <h3 className="text-3xl font-bold text-yellow-400 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 text-[#030406] rounded-full">
                  <i className="ri-customer-service-2-fill text-xl"></i>
                </div>
                Get In Touch
              </h3>
              <div className="space-y-6">
                <div className="bg-[#030406] border border-yellow-400/20 rounded-lg p-6 hover:border-yellow-400 hover:shadow-lg hover:shadow-yellow-400/10 transition-all">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 rounded-full">
                      <i className="ri-phone-fill text-lg"></i>
                    </div>
                    <h4 className="font-bold text-yellow-400 text-lg">Phone</h4>
                  </div>
                  <a href="tel:01603699955" className="text-yellow-400/80 text-lg ml-14 hover:text-yellow-400 transition-colors cursor-pointer">01603 699 955</a>
                </div>
                <div className="bg-[#030406] border border-yellow-400/20 rounded-lg p-6 hover:border-yellow-400 hover:shadow-lg hover:shadow-yellow-400/10 transition-all">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 rounded-full">
                      <i className="ri-mail-fill text-lg"></i>
                    </div>
                    <h4 className="font-bold text-yellow-400 text-lg">Email</h4>
                  </div>
                  <a href="mailto:hello@cherryleafnorwich.co.uk" className="text-yellow-400/80 text-lg ml-14 hover:text-yellow-400 transition-colors cursor-pointer break-all">hello@cherryleafnorwich.co.uk</a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Map */}
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-yellow-400/10 h-[500px] border-2 border-yellow-400/20">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2422.8!2d1.2929!3d52.6309!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d9e3e04ddd168b%3A0x29453f59278957d1!2sCherry%20Leaf%2C%2050%20St%20Giles%20St%2C%20Norwich%20NR2%201LW!5e0!3m2!1sen!2suk!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Cherry Leaf Location - 50 St Giles Street, Norwich"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#030406] border-t-2 border-yellow-400/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6 text-yellow-400">Ready for the Best Coffee in Norwich?</h2>
          <p className="text-xl mb-10 leading-relaxed text-yellow-400/80">
            Join us today at <strong>Chery Leaf</strong>, Norwich's favorite <strong>coffee shop and breakfast spot</strong>. 
            Whether you're a coffee lover or looking for a cozy <strong>Norwich cafe</strong> to relax, we're here for you 
            at our convenient St Giles Street location in the city centre.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="#menu" 
              onClick={(e) => smoothScrollTo(e, '#menu')}
              className="inline-block bg-yellow-400 text-[#030406] hover:bg-yellow-300 px-10 py-4 rounded-full text-lg font-bold transition-all hover:scale-105 shadow-xl cursor-pointer whitespace-nowrap"
            >
              View Full Menu
            </a>
            <a 
              href="#contact" 
              onClick={(e) => smoothScrollTo(e, '#contact')}
              className="inline-block bg-transparent border-2 border-yellow-400 hover:bg-yellow-400/10 text-yellow-400 px-10 py-4 rounded-full text-lg font-bold transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Find Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#030406] text-yellow-400 py-16 px-6 border-t-2 border-yellow-400/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="mb-4">
                <img 
                  src="https://static.readdy.ai/image/2c6a859d2742927cf449d83a19381c12/4c6b31ee4e46e56bb1fe230ca6c02c08.jpeg" 
                  alt="Cherry Leaf Coffee Shop Logo" 
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-yellow-400/70 mb-6 leading-relaxed">
                Your favorite <strong>coffee shop in Norwich</strong>, serving quality and warmth in every cup. 
                We're more than just a <strong>Norwich coffeeshop</strong> – we're a community gathering place where memories are made. 
                Visit us for the best <strong>coffee and breakfast in Norwich city centre</strong>.
              </p>
              <div className="flex gap-3">
                <a 
                  href="https://www.instagram.com/cherryleaf_norwich" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 hover:bg-yellow-400 hover:text-[#030406] rounded-full transition-all hover:scale-110 cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a 
                  href="https://x.com/cherryleafcoffe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 hover:bg-yellow-400 hover:text-[#030406] rounded-full transition-all hover:scale-110 cursor-pointer"
                  aria-label="X (Twitter)"
                >
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6 text-yellow-400">Quick Links</h4>
              <div className="flex flex-col gap-3">
                <a href="#home" onClick={(e) => smoothScrollTo(e, '#home')} className="text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer hover:translate-x-1 inline-block">Home</a>
                <a href="#about" onClick={(e) => smoothScrollTo(e, '#about')} className="text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer hover:translate-x-1 inline-block">About</a>
                <a href="#menu" onClick={(e) => smoothScrollTo(e, '#menu')} className="text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer hover:translate-x-1 inline-block">Menu</a>
                <a href="#testimonials" onClick={(e) => smoothScrollTo(e, '#testimonials')} className="text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer hover:translate-x-1 inline-block">Reviews</a>
                <a href="#contact" onClick={(e) => smoothScrollTo(e, '#contact')} className="text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer hover:translate-x-1 inline-block">Contact</a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6 text-yellow-400">Opening Hours</h4>
              <div className="space-y-2 text-yellow-400/70">
                <p className="font-medium">Mon - Fri</p>
                <p className="text-sm">8:30 AM - 4:00 PM</p>
                <p className="font-medium mt-4">Saturday</p>
                <p className="text-sm">9:00 AM - 4:00 PM</p>
                <p className="font-medium mt-4">Sunday</p>
                <p className="text-sm text-red-400">Closed</p>
              </div>
            </div>
          </div>
          <div className="border-t border-yellow-400/20 pt-8 text-center text-yellow-400/70">
            <p>&copy; 2024 Chery Leaf Coffee Shop. All rights reserved. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}